import {
  Avatar,
  AvatarFallback,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@aircall/ds";
import { AiAgents, User } from "@aircall/react-icons";
import { CSSProperties, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { SPEAKER_VARIANT_COLOR } from "@/constants/style.constants";
import { useCall } from "@/hooks/useCall/useCall";
import { AvatarVariant } from "@/states/call/speakers/speakers.utility.decl";
import {
  SimpleSpeakerAvatarProps,
  SpeakerAvatarProps,
} from "./SpeakerAvatar.decl";
import { getInitials, getTooltipContent } from "./SpeakerAvatar.utils";

// Transparent background with a coloured 1px border + matching text/icon,
// driven by the speaker's DS palette colour. The neutral "default" speaker
// uses DS neutral steps for a slightly darker text than border. Inline styles
// (not utility classes) so they reliably beat the DS fallback's `text-foreground`.
export function fallbackColorStyle(variant: AvatarVariant): CSSProperties {
  if (variant === "default") {
    return {
      color: "var(--color-neutral-600)",
      borderColor: "var(--color-neutral-400)",
    };
  }

  const color = SPEAKER_VARIANT_COLOR[variant];
  return { color, borderColor: color };
}

type AvatarFallbackContentProps = Pick<
  SimpleSpeakerAvatarProps,
  "isVirtualAgent" | "name"
>;

function AvatarFallbackContent({
  isVirtualAgent,
  name,
}: AvatarFallbackContentProps) {
  if (isVirtualAgent) {
    // The virtual agent glyph is always green, regardless of variant.
    return (
      <AiAgents
        className="size-4"
        style={{ color: SPEAKER_VARIANT_COLOR.green }}
      />
    );
  }

  if (name) {
    return (
      <span className="text-[10px] leading-[12px]">{getInitials(name)}</span>
    );
  }

  return <User className="size-4" />;
}

/**
 *
 * @returns Speaker Avatar with text or icon.
 * Forwards ref/props to the Avatar root so it can be used as a Tooltip trigger.
 */
export const SimpleSpeakerAvatar = forwardRef<
  HTMLDivElement,
  SimpleSpeakerAvatarProps
>(function SimpleSpeakerAvatar(
  { name, variant = "default", isVirtualAgent, ...rest },
  ref
) {
  return (
    // `after:border-0` removes the DS Avatar's default neutral ring so only the
    // single colored 1px border below renders, matching the tractor visual.
    <Avatar
      ref={ref}
      size="sm"
      className="self-center after:border-0"
      {...rest}
    >
      <AvatarFallback
        className="border bg-transparent"
        style={fallbackColorStyle(variant)}
      >
        <AvatarFallbackContent isVirtualAgent={isVirtualAgent} name={name} />
      </AvatarFallback>
    </Avatar>
  );
});

/**
 *
 * Render Speaker Avatar according to channel
 */
export function SpeakerAvatar({
  channel,
  shouldDisplayTooltip = true,
}: SpeakerAvatarProps) {
  const { t } = useTranslation();
  const { call } = useCall();

  const speaker = call?.speakers?.get(channel);
  if (!speaker) {
    return null;
  }

  const { variant, fullName, number, companyName, isVirtualAgent } = speaker;

  const trimmedFullName = fullName?.trim();

  const tooltipText = getTooltipContent(t, {
    fullName: trimmedFullName,
    companyName,
    number,
  });

  if (!shouldDisplayTooltip || !tooltipText) {
    return (
      <SimpleSpeakerAvatar
        name={trimmedFullName}
        variant={variant}
        isVirtualAgent={isVirtualAgent}
      />
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <SimpleSpeakerAvatar
            name={trimmedFullName}
            variant={variant}
            isVirtualAgent={isVirtualAgent}
          />
        }
      />
      <TooltipContent side="top" align="center">
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  );
}
